import { useState, useEffect } from "react";

// -- Brand (kept identical to App.jsx so this drops in seamlessly) ---------
const G = "#1B4332";
const GOLD = "#C9A84C";
const BR = "#E5E7EB";

// -- Same engine endpoint as App.jsx -----------------------------------
// IMPORTANT: keep this in sync with ENGINE_URL in App.jsx. If you redeploy
// the GAS Web App and the /exec URL changes, update it in BOTH files.
const ENGINE_URL = "https://script.google.com/macros/s/AKfycbwOLw2KOr52Yt8r5NC8zY7Zb3osv5KBKD4Oa1P88vqaa4mOTGsHlso8KdlxQlp9m0dR/exec";

const inp = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: `1px solid ${BR}`, fontSize: 14, fontFamily: "inherit",
  background: "#fff", color: "#111", outline: "none", boxSizing: "border-box",
};

function Card({ children, style }) {
  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: 22,
      boxShadow: "0 1px 8px rgba(0,0,0,0.07)", marginBottom: 14, ...style }}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", disabled, full }) {
  const styles = {
    primary: { background: G, color: "#fff", border: "none" },
    outline:  { background: "transparent", color: G, border: `1px solid ${G}` },
    gold:     { background: GOLD, color: "#fff", border: "none" },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "10px 24px", borderRadius: 8, fontSize: 13, fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
      fontFamily: "inherit", width: full ? "100%" : "auto", ...styles[variant],
    }}>{children}</button>
  );
}

function Lbl({ children }) {
  return (
    <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280",
      textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 5 }}>
      {children}
    </label>
  );
}

function scColor(s) {
  if (typeof s !== "number") return "#6B7280";
  return s >= 8.5 ? "#15803D" : s >= 7 ? "#92400E" : "#B91C1C";
}

async function fetchProductList() {
  const res = await fetch(ENGINE_URL + "?action=list_products");
  if (!res.ok) throw new Error("Could not load policy list (HTTP " + res.status + ")");
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Could not load policy list");
  return data.products || [];
}

async function fetchScore(productName, insurer) {
  const res = await fetch(ENGINE_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify({ type: "policy_score", action: "score", product: productName, insurer }),
  });
  if (!res.ok) throw new Error("Engine returned HTTP " + res.status);
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Could not score this policy");
  return data;
}

async function fetchPdf(productName, insurer) {
  const res = await fetch(ENGINE_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify({ type: "policy_score", action: "pdf", product: productName, insurer }),
  });
  if (!res.ok) throw new Error("Engine returned HTTP " + res.status);
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Could not generate the PDF report");
  return data;
}

function downloadBase64Pdf(base64, filename) {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "policy-score-report.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function PolicyScoreChecker() {
  const [products, setProducts]     = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError]   = useState("");
  const [selected, setSelected]     = useState("");
  const [checking, setChecking]     = useState(false);
  const [scoreError, setScoreError] = useState("");
  const [result, setResult]         = useState(null);
  const [pdfBusy, setPdfBusy]       = useState(false);
  const [pdfError, setPdfError]     = useState("");

  useEffect(() => {
    fetchProductList()
      .then(list => { setProducts(list); setLoadingList(false); })
      .catch(err => { setListError(err.message); setLoadingList(false); });
  }, []);

  const check = async () => {
    if (selected === "") return;
    const product = products[Number(selected)];
    if (!product) return;
    setChecking(true);
    setScoreError("");
    setResult(null);
    try {
      const data = await fetchScore(product.name, product.insurer);
      setResult(data);
    } catch (err) {
      setScoreError(err.message);
    }
    setChecking(false);
  };

  const download = async () => {
    if (selected === "") return;
    const product = products[Number(selected)];
    if (!product) return;
    setPdfBusy(true);
    setPdfError("");
    try {
      const data = await fetchPdf(product.name, product.insurer);
      downloadBase64Pdf(data.pdf_base64, data.filename);
    } catch (err) {
      setPdfError(err.message);
    }
    setPdfBusy(false);
  };

  return (
    <div>
      <Card>
        <div style={{ fontSize: 20, fontWeight: 700, color: G, marginBottom: 3 }}>Check your policy score</div>
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>
          Select the health insurance policy you already hold to see its Arova HealthShield score
        </div>

        {loadingList && <div style={{ fontSize: 13, color: "#6B7280" }}>Loading policy list...</div>}
        {listError && (
          <div style={{ fontSize: 12, color: "#EF4444", background: "#FEF2F2", padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>
            {listError}
          </div>
        )}

        {!loadingList && !listError && (
          <>
            <Lbl>Your policy</Lbl>
            <select style={inp} value={selected} onChange={e => { setSelected(e.target.value); setResult(null); setScoreError(""); }}>
              <option value="">Select insurer and policy</option>
              {products.map((p, i) => (
                <option key={i} value={i}>{p.insurer} - {p.name}</option>
              ))}
            </select>

            <div style={{ marginTop: 16 }}>
              <Btn onClick={check} disabled={selected === "" || checking} full>
                {checking ? "Checking..." : "Check my policy score"}
              </Btn>
            </div>

            {scoreError && (
              <div style={{ fontSize: 12, color: "#EF4444", background: "#FEF2F2", padding: "8px 12px", borderRadius: 8, marginTop: 12 }}>
                {scoreError}
              </div>
            )}
          </>
        )}
      </Card>

      {result && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: G }}>{result.insurer}</div>
              <div style={{ fontSize: 13, color: "#6B7280" }}>{result.name}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, justifyContent: "flex-end" }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: scColor(result.composite_score), lineHeight: 1 }}>
                  {typeof result.composite_score === "number" ? result.composite_score.toFixed(1) : "--"}
                </div>
                {result.composite_grade && (
                  <div style={{ fontSize: 14, fontWeight: 700, color: GOLD }}>{result.composite_grade}</div>
                )}
              </div>
              <div style={{ fontSize: 10, color: "#9CA3AF" }}>out of 10</div>
            </div>
          </div>

          <div style={{ fontSize: 13, lineHeight: 1.6, padding: "12px 14px",
            background: "#F9FAFB", borderLeft: `3px solid ${GOLD}`, borderRadius: "0 8px 8px 0", marginBottom: 16 }}>
            {result.summary}
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: G, textTransform: "uppercase",
            letterSpacing: "0.05em", marginBottom: 8 }}>Category breakdown</div>
          <div style={{ marginBottom: 16 }}>
            {(result.category_scores || []).map((c, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "6px 0", borderBottom: i < result.category_scores.length - 1 ? `1px solid ${BR}` : "none" }}>
                <span style={{ fontSize: 12, color: "#374151" }}>{c.label}</span>
                <span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: scColor(c.score) }}>{c.score.toFixed(1)}</span>
                  {c.grade && <span style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF" }}>{c.grade}</span>}
                </span>
              </div>
            ))}
          </div>

          {result.verdict && (
            <div style={{ fontSize: 13, fontWeight: 700, color: G, padding: "10px 14px",
              background: "#F0FDF4", borderRadius: 8, marginBottom: 16 }}>
              {result.verdict}
            </div>
          )}

          {result.strengths && result.strengths.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: G, textTransform: "uppercase",
                letterSpacing: "0.05em", marginBottom: 6 }}>Key strengths</div>
              {result.strengths.map((s, i) => (
                <div key={i} style={{ fontSize: 12, color: "#374151", padding: "3px 0 3px 10px",
                  borderLeft: "2px solid #22C55E", marginBottom: 3 }}>{s}</div>
              ))}
            </div>
          )}

          {result.watch_outs && result.watch_outs.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#92400E", textTransform: "uppercase",
                letterSpacing: "0.05em", marginBottom: 6 }}>Watch-outs</div>
              {result.watch_outs.map((w, i) => (
                <div key={i} style={{ fontSize: 12, color: "#374151", padding: "3px 0 3px 10px",
                  borderLeft: "2px solid #FCD34D", marginBottom: 3 }}>{w}</div>
              ))}
            </div>
          )}

          <Btn variant="gold" onClick={download} disabled={pdfBusy} full>
            {pdfBusy ? "Generating PDF..." : "Download policy report (PDF)"}
          </Btn>
          {pdfError && (
            <div style={{ fontSize: 12, color: "#EF4444", background: "#FEF2F2", padding: "8px 12px", borderRadius: 8, marginTop: 10 }}>
              {pdfError}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
